import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Plus, RefreshCw } from 'lucide-react';
import { FeedbackContext } from '../../../../contexts/FeedbackContext';
import InfiniteSelect from '../../../../components/InfiniteSelect';
import ApiService from '../../../../services/ApiService';
import {
  buildConditionInstallments,
  formatInstallmentPosition,
} from '../mappers';
import { formatCurrencyBRL, parseCurrencyValue } from '../financialFormatters';
import {
  FINANCIAL_ENTRY_RECURRENCE,
  FINANCIAL_ENTRY_RECURRENCE_SIMPLE_OPTIONS,
  FINANCIAL_ENTRY_TYPE,
  MONTH_DAY_OPTIONS,
  WEEKDAY_OPTIONS,
} from '../constants';
import { addDays, parseConditionToOffsets } from '../../OrderForm/utils';
import financialEntryApi from '../api';
import { paymentMethodLabel, registerPaymentMethodCatalog } from '../../../../utils/paymentMethodLabels';
import {
  RecurrenceAnnualPicker,
  RecurrenceChipPicker,
} from './RecurrenceDayPicker';
import {
  AddInstallmentButton,
  CardTitle,
  CommissionCell,
  DeleteIconButton,
  DeleteTrashIcon,
  FormGrid,
  FormSearchableSelectWrap,
  InstallmentLabel,
  InstallmentRow,
  InstallmentsHeader,
  InstallmentsSummary,
  ParcelasPanel,
  ParcelasPanelHeader,
  ParcelasTitle,
  Slider,
  StyledCard,
  StyledFormGroup,
  SwitchInput,
  SwitchLabel,
  ToggleSwitchContainer,
  UpdateInstallmentsButton,
} from './style';
import FormOptionSelect from './FormOptionSelect';
import FormCurrencyField from './FormCurrencyField';
import FormPercentField from './FormPercentField';
import { FinancialEntryFormSchemaType } from './schema';

interface PaymentSectionProps {
  isEdit?: boolean;
}

const PaymentSection: React.FC<PaymentSectionProps> = ({ isEdit = false }) => {
  const { control, watch, setValue, getValues, formState: { errors } } = useFormContext<FinancialEntryFormSchemaType>();
  const { setFeedback } = useContext(FeedbackContext);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<
    { value: number; label: string }[]
  >([]);

  const recurrence = watch('recurrence');
  const entryType = watch('type');
  const totalValue = watch('totalValue');
  const paymentConditionId = watch('paymentConditionId');
  const paymentConditionName = watch('paymentConditionName');
  const installments = watch('installments');
  const isRecurring = (watch('isRecurring') ?? false) && !isEdit;
  const showCommission = entryType !== FINANCIAL_ENTRY_TYPE.EXPENSE;
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'installments',
  });
  const didInitInstallments = useRef(false);
  const lastInstallmentKey = useRef('');

  useEffect(() => {
    financialEntryApi
      .getPaymentMethods()
      .then((methods) => {
        registerPaymentMethodCatalog(methods);
        setPaymentMethodOptions(
          methods.map((m) => ({ value: m.id, label: paymentMethodLabel(m.name) })),
        );
      })
      .catch(() => {
        setFeedback({
          message: 'Não foi possível carregar as formas de pagamento.',
          isOpen: true,
          statusOk: false,
        });
      });
  }, [setFeedback]);

  // Fora da recorrência, a condição (ex: `30/60/90`) divide o valor total.
  // A primeira execução preserva as parcelas já carregadas na edição.
  useEffect(() => {
    if (isRecurring) return;

    const parsedTotal = parseCurrencyValue(totalValue);
    const key = `${paymentConditionId ?? ''}|${paymentConditionName ?? ''}|${parsedTotal ?? ''}`;

    if (!didInitInstallments.current) {
      didInitInstallments.current = true;
      lastInstallmentKey.current = key;
      return;
    }

    if (key === lastInstallmentKey.current) return;
    lastInstallmentKey.current = key;
    if (!parsedTotal || !paymentConditionName) return;

    const current = getValues('installments') ?? [];
    const generated = buildConditionInstallments(
      parsedTotal,
      paymentConditionName,
      current.map((inst) => ({
        id: inst.id,
        value: inst.value ?? '',
        dueDate: inst.dueDate ?? '',
        commission: inst.commission ?? '',
        hasCommission: inst.hasCommission ?? false,
        installmentNumber: inst.installmentNumber ?? '',
      })),
    );

    setValue('installments', generated, { shouldDirty: true });
  }, [isRecurring, totalValue, paymentConditionId, paymentConditionName, getValues, setValue]);

  useEffect(() => {
    if (isRecurring) return;
    const total = fields.length;
    const current = getValues('installments') ?? [];
    current.forEach((installment, index) => {
      const label = formatInstallmentPosition(index, total);
      if (installment.installmentNumber === label) return;
      setValue(`installments.${index}.installmentNumber`, label, { shouldDirty: false });
    });
  }, [fields.length, isRecurring, getValues, setValue]);

  const handleRefreshDueDates = () => {
    const offsets = parseConditionToOffsets(paymentConditionName);
    const current = getValues('installments') ?? [];
    setValue(
      'installments',
      current.map((installment, index) => {
        const offset = offsets[index] ?? offsets[offsets.length - 1] ?? 0;
        return {
          ...installment,
          dueDate: addDays(undefined, offset),
        };
      }),
      { shouldDirty: true },
    );
  };

  const handleAddInstallment = () => {
    const nextCount = fields.length + 1;
    append({
      value: '',
      dueDate: '',
      commission: '',
      hasCommission: false,
      installmentNumber: formatInstallmentPosition(fields.length, nextCount),
    });
  };

  const installmentSum = (installments ?? []).reduce(
    (sum, installment) => sum + (parseCurrencyValue(installment.value) ?? 0),
    0,
  );
  const parsedTotalValue = parseCurrencyValue(totalValue) ?? 0;
  const installmentsMismatch =
    fields.length > 0 && Math.abs(installmentSum - parsedTotalValue) > 0.01;

  const handleToggleRecurring = (checked: boolean) => {
    setValue('isRecurring', checked, { shouldDirty: true });
  };

  const fetchPaymentConditions = useCallback(
    async (limit: number, offset: number, search?: string) => {
      const page = Math.floor(offset / limit) + 1;
      const response = await ApiService.steelOrders.getPaymentConditions({
        page,
        limit,
        search,
      });
      return response.data?.data ?? [];
    },
    [],
  );

  const createPaymentCondition = useCallback(async (name: string) => {
    const response = await ApiService.steelOrders.createPaymentCondition({ name });
    return response.data;
  }, []);

  const recurrenceStartDate = watch('recurrenceStartDate');
  const recurrenceDaysOfWeek = watch('recurrenceDaysOfWeek') ?? [];
  const recurrenceDueDays = watch('recurrenceDueDays') ?? [];
  const recurrenceAnnualDates = watch('recurrenceAnnualDates') ?? [];

  /** Calcula a próxima data que cai num determinado dia da semana (0=Dom…6=Sáb). */
  const nextDateForWeekday = (targetDay: number, from?: string): string => {
    const base = from ? new Date(from + 'T00:00:00') : new Date();
    const diff = ((targetDay - base.getDay()) + 7) % 7;
    const d = new Date(base);
    d.setDate(d.getDate() + diff);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  /** Calcula a próxima data para um dia do mês específico. */
  const nextDateForMonthDay = (dueDay: number, from?: string): string => {
    const base = from ? new Date(from + 'T00:00:00') : new Date();
    const year = base.getFullYear();
    const month = base.getMonth();
    const maxDays = new Date(year, month + 1, 0).getDate();
    const safeDay = Math.min(dueDay, maxDays);
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(safeDay).padStart(2, '0')}`;
  };

  const handleDaysOfWeekChange = (values: number[]) => {
    setValue('recurrenceDaysOfWeek', values, { shouldDirty: true });
    // Ao selecionar o primeiro dia, ajusta startDate para a próxima ocorrência desse dia.
    if (values.length === 1) {
      const newDate = nextDateForWeekday(values[0], recurrenceStartDate || undefined);
      setValue('recurrenceStartDate', newDate, { shouldDirty: true });
    }
  };

  const handleDueDaysChange = (values: number[]) => {
    setValue('recurrenceDueDays', values, { shouldDirty: true });
    // Ao selecionar o primeiro dia, ajusta startDate para esse dia do mês corrente.
    if (values.length === 1) {
      const newDate = nextDateForMonthDay(values[0], recurrenceStartDate || undefined);
      setValue('recurrenceStartDate', newDate, { shouldDirty: true });
    }
  };

  return (
    <StyledCard>
      <CardTitle>Pagamento</CardTitle>

      {!isEdit && (
        <FormGrid cols={3} style={{ marginBottom: 16 }}>
          <StyledFormGroup>
            <label>Lançamento recorrente</label>
            <ToggleSwitchContainer>
              <SwitchLabel>
                <Controller
                  name="isRecurring"
                  control={control}
                  render={({ field }) => (
                    <SwitchInput
                      type="checkbox"
                      checked={!!field.value}
                      onChange={(e) => handleToggleRecurring(e.target.checked)}
                    />
                  )}
                />
                <Slider className="slider" />
              </SwitchLabel>
            </ToggleSwitchContainer>
          </StyledFormGroup>

          {isRecurring && (
            <>
              {/* Frequência */}
              <Controller
                name="recurrence"
                control={control}
                render={({ field }) => (
                  <FormOptionSelect
                    label="Frequência"
                    value={field.value ?? null}
                    onChange={(val) => {
                      field.onChange(val);
                      // Limpa seleções ao trocar frequência
                      setValue('recurrenceDaysOfWeek', []);
                      setValue('recurrenceDueDays', []);
                      setValue('recurrenceAnnualDates', []);
                    }}
                    options={FINANCIAL_ENTRY_RECURRENCE_SIMPLE_OPTIONS}
                    hasError={!!errors.recurrence}
                  />
                )}
              />

              {/* Semanal: chips dos dias da semana */}
              {recurrence === FINANCIAL_ENTRY_RECURRENCE.WEEKLY && (
                <Controller
                  name="recurrenceDaysOfWeek"
                  control={control}
                  render={() => (
                    <RecurrenceChipPicker
                      label="Dias da semana"
                      options={WEEKDAY_OPTIONS}
                      selected={recurrenceDaysOfWeek}
                      onChange={handleDaysOfWeekChange}
                      hasError={!!errors.recurrenceDaysOfWeek}
                    />
                  )}
                />
              )}

              {/* Mensal: chips dos dias do mês */}
              {recurrence === FINANCIAL_ENTRY_RECURRENCE.MONTHLY && (
                <Controller
                  name="recurrenceDueDays"
                  control={control}
                  render={() => (
                    <RecurrenceChipPicker
                      label="Dias do mês"
                      options={MONTH_DAY_OPTIONS}
                      selected={recurrenceDueDays}
                      onChange={handleDueDaysChange}
                      hasError={!!errors.recurrenceDueDays}
                    />
                  )}
                />
              )}

              {/* Data de início (Semanal / Mensal) ou Datas do Ano (Anual) */}
              {recurrence === FINANCIAL_ENTRY_RECURRENCE.YEARLY ? (
                <Controller
                  name="recurrenceAnnualDates"
                  control={control}
                  render={() => (
                    <RecurrenceAnnualPicker
                      dates={recurrenceAnnualDates}
                      hasError={!!errors.recurrenceAnnualDates}
                      onChange={(dates) =>
                        setValue('recurrenceAnnualDates', dates, { shouldDirty: true })
                      }
                    />
                  )}
                />
              ) : (
                <StyledFormGroup>
                  <label>Data de início</label>
                  <Controller
                    name="recurrenceStartDate"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="date"
                        value={field.value ?? ''}
                        aria-label="Data de início da recorrência"
                        data-field-error={
                          errors.recurrenceStartDate ? 'true' : undefined
                        }
                      />
                    )}
                  />
                </StyledFormGroup>
              )}

              {/* Término */}
              <StyledFormGroup>
                <label>Término</label>
                <Controller
                  name="recurrenceCount"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="number"
                      min={1}
                      value={field.value ?? ''}
                      placeholder="Pra sempre"
                      aria-label="Quantidade de ocorrências"
                    />
                  )}
                />
              </StyledFormGroup>
            </>
          )}
        </FormGrid>
      )}

      <FormGrid cols={3}>
        <div>
          <Controller
            name="paymentMethodId"
            control={control}
            render={({ field }) => (
              <FormOptionSelect
                label="Forma de pagamento"
                required
                value={field.value ?? null}
                onChange={field.onChange}
                options={paymentMethodOptions}
                hasError={!!errors.paymentMethodId}
              />
            )}
          />
        </div>

        <StyledFormGroup>
          <label>{isRecurring ? 'Valor por ocorrência' : 'Valor total'}</label>
          <Controller
            name="totalValue"
            control={control}
            render={({ field }) => (
              <FormCurrencyField
                id="entry-total-value"
                name={field.name}
                value={field.value ?? ''}
                onChange={field.onChange}
                hasError={!!errors.totalValue}
              />
            )}
          />
        </StyledFormGroup>

        {!isRecurring && (
          <StyledFormGroup>
            <label>Condição</label>
            <Controller
              name="paymentConditionId"
              control={control}
              render={() => (
                <FormSearchableSelectWrap $hasError={!!errors.paymentConditionId}>
                  <InfiniteSelect
                    mode="crud"
                    placeholder="Selecione ou crie (ex: 30/60/90)"
                    searchPlaceholder="Buscar condição..."
                    minCreateLength={2}
                    allowDelete={false}
                    showSelectedBelow={false}
                    value={
                      paymentConditionId
                        ? { id: paymentConditionId, name: paymentConditionName ?? '' }
                        : null
                    }
                    getOptionLabel={(option: any) => option.name}
                    hasError={!!errors.paymentConditionId}
                    fetchFunction={fetchPaymentConditions}
                    onItemCreate={createPaymentCondition}
                    onSelect={(option: any) => {
                      setValue('paymentConditionId', option?.id ?? null, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                      setValue('paymentConditionName', option?.name ?? '', {
                        shouldDirty: true,
                      });
                    }}
                  />
                </FormSearchableSelectWrap>
              )}
            />
          </StyledFormGroup>
        )}
      </FormGrid>

      {!isRecurring && (
        <ParcelasPanel>
          <ParcelasPanelHeader>
            <ParcelasTitle>Parcelas</ParcelasTitle>
            <UpdateInstallmentsButton type="button" onClick={handleRefreshDueDates}>
              <RefreshCw size={16} />
              Atualizar data
            </UpdateInstallmentsButton>
          </ParcelasPanelHeader>

          {fields.length > 0 && (
            <>
              <InstallmentsHeader $hideCommission={!showCommission}>
                <span>Nº</span>
                <span>Valor</span>
                <span>Vencimento</span>
                {showCommission && <span>Comissão</span>}
                <span />
              </InstallmentsHeader>

              {fields.map((field, index) => {
                const hasCommission = installments?.[index]?.hasCommission ?? false;
                return (
                  <InstallmentRow key={field.id} $hideCommission={!showCommission}>
                    <InstallmentLabel>{index + 1}</InstallmentLabel>

                    <StyledFormGroup>
                      <Controller
                        name={`installments.${index}.value`}
                        control={control}
                        render={({ field: valueField }) => (
                          <FormCurrencyField
                            id={`installment-value-${index}`}
                            name={valueField.name}
                            value={valueField.value ?? ''}
                            onChange={valueField.onChange}
                          />
                        )}
                      />
                    </StyledFormGroup>

                    <StyledFormGroup>
                      <Controller
                        name={`installments.${index}.dueDate`}
                        control={control}
                        render={({ field: dueDateField }) => (
                          <input
                            {...dueDateField}
                            type="date"
                            value={dueDateField.value ?? ''}
                            aria-label={`Vencimento da parcela ${index + 1}`}
                          />
                        )}
                      />
                    </StyledFormGroup>

                    {showCommission && (
                      <CommissionCell>
                        <ToggleSwitchContainer>
                          <SwitchLabel>
                            <Controller
                              name={`installments.${index}.hasCommission`}
                              control={control}
                              render={({ field: commissionToggle }) => (
                                <SwitchInput
                                  type="checkbox"
                                  checked={!!commissionToggle.value}
                                  onChange={(event) => {
                                    commissionToggle.onChange(event.target.checked);
                                    if (!event.target.checked) {
                                      setValue(`installments.${index}.commission`, '');
                                    }
                                  }}
                                />
                              )}
                            />
                            <Slider className="slider" />
                          </SwitchLabel>
                        </ToggleSwitchContainer>
                        {hasCommission && (
                          <StyledFormGroup style={{ flex: 1, margin: 0 }}>
                            <Controller
                              name={`installments.${index}.commission`}
                              control={control}
                              render={({ field: commissionField }) => (
                                <FormPercentField
                                  id={`installment-commission-${index}`}
                                  name={commissionField.name}
                                  value={commissionField.value ?? ''}
                                  onChange={commissionField.onChange}
                                  aria-label={`Comissão da parcela ${index + 1}`}
                                />
                              )}
                            />
                          </StyledFormGroup>
                        )}
                      </CommissionCell>
                    )}

                    <DeleteIconButton
                      type="button"
                      title="Remover parcela"
                      onClick={() => remove(index)}
                    >
                      <DeleteTrashIcon />
                    </DeleteIconButton>
                  </InstallmentRow>
                );
              })}
            </>
          )}

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <AddInstallmentButton
              type="button"
              style={{ width: 'auto' }}
              onClick={handleAddInstallment}
            >
              <Plus size={16} />
              Adicionar parcela
            </AddInstallmentButton>
          </div>

          {fields.length > 0 && (
            <InstallmentsSummary $mismatch={installmentsMismatch}>
              <span>Soma das parcelas: {formatCurrencyBRL(installmentSum)}</span>
              <span>·</span>
              <span>Total: {formatCurrencyBRL(parsedTotalValue)}</span>
            </InstallmentsSummary>
          )}
        </ParcelasPanel>
      )}
    </StyledCard>
  );
};

export default PaymentSection;
